import React, { useEffect, useRef, useState } from 'react'
import { Scroll, MagnifyingGlass, SortAscending, X } from '@phosphor-icons/react'
import { Icon, IconButton, Input } from '@shopflo/ui'
import { GenericSelectionModal } from 'src/components/common/select-component/GenericSelectionModal'
import ReportList from 'src/components/Reports/ReportList'
import PauseReportDialog from 'src/components/Reports/PauseReportDialog'
import axios from 'axios'
import Cookies from 'js-cookie'
import { apiURI, constants } from 'src/lib/utils/constants'
import { ReportProvider } from 'src/contexts/ReportContext'
import { Report } from 'src/components/Reports/types'
import { normalizeReports } from 'src/components/Reports/parser'
import useSWR from 'swr'
import { useReportsList } from 'src/lib/hooks/useReportsList'
import ReportsFilterBar from 'src/components/Reports/ReportsFilterBar'
import GoogleSheetsBanner from 'src/components/Reports/GoogleSheetsBanner'
import GenerateReportsButton from 'src/components/Reports/GenerateReportsButton'
import CreateReportDialog from 'src/components/Reports/CreateReportDialog'
import { DownloadReportConfig } from 'src/components/common/export-component/types'
import { addDays, addMinutes, startOfDay, endOfDay, isToday } from 'date-fns'
import { exportReport } from 'src/lib/core/api-methods'
import { getRequest, postRequest, staticOptions } from 'src/lib/core/api-client'
import { toUTC, toUTCTimeStamp, fromUTC, formatDateToUTC } from 'src/lib/utils/helpers'
import { parseCronExpression } from 'src/components/common/export-component/steps/two/recurring'
import { errorToast, successToast } from 'src/lib/utils/toasters'
import { useLocale } from 'src/lib/hooks/useLocal'
import useGTM from 'src/lib/hooks/useGTM'

const sortOptions = [
  { key: 'NEWEST', value: 'Newest First' },
  { key: 'OLDEST', value: 'Oldest First' }
]

const DownloadsPage = () => {
  const { t } = useLocale()
  const { sendGTMTrigger } = useGTM()
  const [uiState, setUiState] = useState({
    dialogConfig: {
      isOpen: false,
      type: 'pause' as 'pause' | 'resume',
      reportId: '',
      runId: ''
    },
    reportTypeFilter: '',
    frequencyFilter: '',
    headerSearch: '',
    sortOrder: 'NEWEST',
    isSearchExpanded: false,
    searchQuery: '',
    selectedReportType: '',
    showExportDialog: false,
    isEditMode: false,
    editReportId: '',
    // Whether the report being edited is currently paused — so we don't silently resume it on Save.
    editReportPaused: false,
    highlightedReportId: '',
    isStepValid: true,
    isSubmitting: false
  })

  // Measure the sticky header so the filter bar can stick just beneath it (no magic numbers).
  const headerRef = useRef<HTMLDivElement>(null)
  const [headerHeight, setHeaderHeight] = useState(0)
  useEffect(() => {
    const measure = () => setHeaderHeight(headerRef.current?.offsetHeight ?? 0)
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])

  // Transiently highlight a freshly created/updated report row for 2s (fades out after).
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => {
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current)
  }, [])
  const highlightRow = (reportId: string) => {
    if (!reportId) return
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current)
    setUiState(prev => ({ ...prev, highlightedReportId: reportId }))
    highlightTimerRef.current = setTimeout(() => setUiState(prev => ({ ...prev, highlightedReportId: '' })), 2000)
  }

  const [config, setConfig] = useState<DownloadReportConfig>({
    step1: {
      exportType: 'orders',
      selectedColumns: []
    },
    step2: {
      exportFrequency: 'once',
      dateRange: {
        startDate: addDays(new Date(), -2).toISOString(),
        endDate: addDays(new Date(), -1).toISOString()
      },
      schedule: {
        frequency: 'daily',
        hour: 0,
        day: 0,
        cronExpression: '0 0 * * ? *',
        scheduledStartDate: new Date().toISOString()
      },
      emails: [''],
      reportName: ''
    }
  })

  const {
    reports,
    hasMore,
    size,
    setSize,
    isValidating: isReportsValidating,
    isInitialLoading,
    isLoadingMore,
    mutate: mutateReports
  } = useReportsList(
    uiState.reportTypeFilter,
    uiState.sortOrder === 'OLDEST' ? 'ASC' : 'DESC',
    uiState.headerSearch,
    uiState.frequencyFilter
  )

  const reportsRef = useRef(reports)
  reportsRef.current = reports
  const preCreateReportIdsRef = useRef<Set<string> | null>(null)

  useEffect(() => {
    const prevIds = preCreateReportIdsRef.current
    if (!prevIds || isReportsValidating) return
    preCreateReportIdsRef.current = null
    const freshReport = reports.find(report => !prevIds.has(report.reportId))
    if (!freshReport) return
    setUiState(prev => ({ ...prev, highlightedReportId: freshReport.reportId }))
    const timer = setTimeout(() => setUiState(prev => ({ ...prev, highlightedReportId: '' })), 2000)
    return () => clearTimeout(timer)
  }, [reports, isReportsValidating])

  const fetchColumnData = async (url: string) => {
    if (!uiState.selectedReportType) return null
    const data: any = await getRequest(url, apiURI.CHECKOUT_NODE)
    return data?.data?.columns
  }

  const { data: columnData, isValidating } = useSWR(
    `/dashboard/report/column-list?type=${uiState.selectedReportType}`,
    (url: string) => fetchColumnData(url),
    staticOptions
  )


  const isExportingTodaysReport = config.step1.exportType === 'today'

  const handleTodayExport = async () => {
    try {
      if (!config.step2.emails || !config.step2.emails.length) {
        errorToast(t('report.download.failed'))
        return
      }
      setUiState(prev => ({ ...prev, isSubmitting: true }))
      const response: any = await exportReport(
        uiState.selectedReportType === 'abandoned_checkouts' ? 'all_checkouts' : uiState.selectedReportType,
        config.step2.emails[0],
        toUTCTimeStamp(startOfDay(new Date())),
        toUTCTimeStamp(endOfDay(new Date()))
      )
      if (response?.error) {
        throw new Error(response.error)
      }
      successToast(t('report.download.success.today'))
      mutateReports()
      handleClose()
    } catch (error) {
      errorToast(t('report.download.failed'))
      console.error(error)
    } finally {
      setUiState(prev => ({ ...prev, isSubmitting: false }))
    }
  }

  // Report config payload shared by create (scheduler-create) and update (scheduler-status).
  type ReportPayload = {
    name: string
    frequencyType: string
    columns: readonly string[]
    type: any
    emails?: string[]
    reportDateRange?: { start: string; end: string }
    scheduleStartDate?: string
    frequency?: string
  }

  const buildReportPayload = (): ReportPayload => {
    const payload: ReportPayload = {
      name: config.step2.reportName,
      frequencyType: config.step2.exportFrequency.toUpperCase(),
      columns: config.step1.selectedColumns,
      type: config.step1.exportType
    }

    if (config.step2.emails.length) {
      payload.emails = config.step2.emails
    }

    if (
      config.step2.exportFrequency === 'once' &&
      config.step2.dateRange.startDate &&
      config.step2.dateRange.endDate
    ) {
      // start = picked From at 00:00 IST (day before at UTC 18:30). end = the day AFTER the picked To at
      // 00:00 IST (picked To at UTC 18:30), i.e. the exclusive boundary, so the picked end day is fully
      // covered. The read side steps end back a day to show the picked To.
      payload.reportDateRange = {
        start: formatDateToUTC(addDays(new Date(config.step2.dateRange.startDate), -1)),
        end: formatDateToUTC(new Date(config.step2.dateRange.endDate))
      }
    }

    if (config.step2.exportFrequency === 'recurring' && config.step2.schedule?.scheduledStartDate) {
      const scheduledDate = new Date(config.step2.schedule.scheduledStartDate)
      payload.scheduleStartDate = isToday(scheduledDate)
        ? toUTC(addMinutes(new Date(), 5).toISOString())
        : toUTC(config.step2.schedule.scheduledStartDate)
      payload.frequency = config.step2.schedule?.cronExpression
    }

    return payload
  }

  const handleSubmit = async () => {
    if (isExportingTodaysReport) {
      await handleTodayExport()
      return
    }

    try {
      setUiState(prev => ({ ...prev, isSubmitting: true }))
      const payload = buildReportPayload()
      await postRequest(`/dashboard/report/scheduler-create`, apiURI.CHECKOUT_NODE, payload)
      successToast(t('report.download.success'))
      sendGTMTrigger('report_export_generated', 'create', {
        reportType: uiState.selectedReportType
      })
      // Snapshot the current report ids, then revalidate; the effect above highlights the new row.
      preCreateReportIdsRef.current = new Set(reportsRef.current.map(report => report.reportId))
      mutateReports()
      handleClose()
    } catch (error) {
      errorToast(t('report.download.failed'))
      console.error(error)
    } finally {
      setUiState(prev => ({ ...prev, isSubmitting: false }))
    }
  }

  const handleClose = () => {
    setUiState(prev => ({
      ...prev,
      showExportDialog: false,
      selectedReportType: '',
      isEditMode: false,
      editReportId: '',
      isStepValid: false
    }))
  }

  // Prefill the Create/Edit dialog config from a report object.
  const applyReportToConfig = (report: Report, forceFrequency?: 'once' | 'recurring') => {
    // start decodes straight back to picked From; end is the exclusive next-day boundary, so step back a
    // day to prefill the picked To.
    const editStartDate = fromUTC(report.startDate)?.toISOString()
    const editEndRaw = fromUTC(report.endDate)
    const editEndDate = editEndRaw ? addDays(editEndRaw, -1).toISOString() : undefined
    setConfig(prev => ({
      ...prev,
      step1: {
        ...prev.step1,
        exportType: report.type as DownloadReportConfig['step1']['exportType'],
        selectedColumns: report.columns ?? []
      },
      step2: {
        ...prev.step2,
        reportName: report.name,
        emails: report.emails ?? [],
        exportFrequency: forceFrequency ?? (report.frequencyType === 'RECURRING' ? 'recurring' : 'once'),
        dateRange: {
          startDate: editStartDate || prev.step2.dateRange.startDate,
          endDate: editEndDate || prev.step2.dateRange.endDate
        },
        schedule: {
          frequency: prev.step2.schedule?.frequency ?? 'daily',
          hour: prev.step2.schedule?.hour ?? 0,
          day: prev.step2.schedule?.day ?? 0,
          cronExpression: report.frequency ?? prev.step2.schedule?.cronExpression ?? '0 0 * * ? *',
          // Schedule start is `runStartAt` — convert it back from the stored `toUTC` form so the picker
          // shows the same date/time that was set. (runs[0].startDate is the run's covered data range.)
          scheduledStartDate:
            fromUTC(report.runStartAt)?.toISOString() || report.runs[0]?.startDate || new Date().toISOString()
        }
      }
    }))
  }

  // Fetch the authoritative report from the single-report endpoint and re-prefill the edit form.
  const refreshEditReport = async (reportId: string, forceFrequency?: 'once' | 'recurring') => {
    try {
      const response = await axios.get(`/dashboard/report/${reportId}`, {
        baseURL: apiURI.CHECKOUT_NODE,
        headers: { Authorization: Cookies.get(constants.AUTH_COOKIE_CLIENT) ?? '' }
      })
      // The endpoint returns the report object directly; tolerate a { data: report } wrapper too.
      const raw = response.data?.report_id ? response.data : response.data?.data
      if (!raw?.report_id) return
      const [fresh] = normalizeReports([raw])
      if (fresh) applyReportToConfig(fresh, forceFrequency)
    } catch (error) {
      console.error('Failed to load report for edit', error)
    }
  }

  // Opens the Create Report dialog in edit mode. Prefills instantly from the loaded list row, then
  // refreshes from the single-report endpoint (`GET /dashboard/report/{id}`) — the source of truth.
  // `forceFrequency` locks the frequency (used by "Set up Recurring Report" on a one-time report).
  const openEditDialog = (report: Report, forceFrequency?: 'once' | 'recurring') => {
    applyReportToConfig(report, forceFrequency)
    setUiState(prev => ({
      ...prev,
      isEditMode: true,
      editReportId: report.reportId,
      editReportPaused: report.status === 'paused',
      selectedReportType: report.type,
      showExportDialog: true
    }))
    refreshEditReport(report.reportId, forceFrequency)
  }

  const handleEditReport = (report: Report) => openEditDialog(report)
  // One-time report → convert to recurring: open the edit modal locked to Recurring.
  const handleSetupRecurring = (report: Report) => openEditDialog(report, 'recurring')

  const handleEditSubmit = async () => {
    // Guard the column-wipe race: never send an update with an empty column set (e.g. Save pressed
    // before the single-report detail fetch has prefilled the columns).
    if (!config.step1.selectedColumns.length) {
      errorToast(t('report.dialog.columns_loading'))
      return
    }
    try {
      setUiState(prev => ({ ...prev, isSubmitting: true }))
      const cron = config.step2.schedule?.cronExpression
      const payload = {
        ...buildReportPayload(),
        reportId: uiState.editReportId,
        // Don't silently resume a paused report on edit — keep it paused; only active reports 'start'.
        action: uiState.editReportPaused ? 'pause' : 'start',
        expiryLimit: null,
        isMonthly: cron ? parseCronExpression(cron.replace('?', '*')).frequency === 'monthly' : false
      }
      await axios.put('/dashboard/report/scheduler-status', payload, {
        baseURL: apiURI.CHECKOUT_NODE,
        headers: { Authorization: Cookies.get(constants.AUTH_COOKIE_CLIENT) ?? '' }
      })
      const editedReportId = uiState.editReportId
      successToast(t('report.download.success'))
      mutateReports()
      handleClose()
      highlightRow(editedReportId)
    } catch (error: any) {
      const backendMessage = error?.response?.data?.error || error?.data?.error
      errorToast(backendMessage)
      console.error(error)
    } finally {
      setUiState(prev => ({ ...prev, isSubmitting: false }))
    }
  }

  const handleReportSelect = (reportId: string) => {
    setUiState(prev => ({
      ...prev,
      selectedReportType: reportId,
      isEditMode: false,
      editReportId: '',
      showExportDialog: true
    }))
    // Reset every field to its default state for a brand-new report.
    setConfig({
      step1: {
        exportType: reportId as 'orders' | 'abandoned_checkouts' | 'all_checkouts' | 'today',
        selectedColumns: []
      },
      step2: {
        exportFrequency: 'once',
        dateRange: {
          // Today is disabled in the picker, so From/To default to the two most recent selectable days.
          startDate: addDays(new Date(), -2).toISOString(),
          endDate: addDays(new Date(), -1).toISOString()
        },
        schedule: {
          frequency: 'daily',
          hour: 0,
          day: 0,
          cronExpression: '0 0 * * ? *',
          scheduledStartDate: new Date().toISOString()
        },
        emails: [],
        reportName: ''
      }
    })
  }
  const handleTypeFilterChange = (value: string) => {
    setUiState(prev => ({ ...prev, reportTypeFilter: value }))
  }
  const handleFrequencyFilterChange = (value: string) => {
    setUiState(prev => ({ ...prev, frequencyFilter: value }))
  }

  return (
    <ReportProvider>
      <div className='flex flex-col px-12 pb-10'>
        <div ref={headerRef} className='sticky top-0 z-10 flex items-center justify-between bg-zinc-50 pb-4 pt-5'>
          <div className='flex items-center gap-1'>
            <Icon IconComponent={Scroll} size='md' className='text-zinc-500' iconProps={{ weight: 'duotone' }} />
            <h1 className='text-text-lg-semibold text-zinc-800'>Reports</h1>
          </div>
          <div className='flex items-center gap-0.5'>
            {uiState.isSearchExpanded ? (
              <Input
                type='text'
                value={uiState.headerSearch}
                onChange={e => setUiState(prev => ({ ...prev, headerSearch: e.target.value }))}
                placeholder='Search reports'
                variant='outline'
                dimension='sm'
                aria-label='Search reports'
                startAdornment={<Icon IconComponent={MagnifyingGlass} size='sm' className='text-zinc-500' />}
                endAdornment={
                  <IconButton
                    IconComponent={X}
                    size='xs'
                    variant='ghost'
                    onClick={() =>
                      setUiState(prev => ({ ...prev, headerSearch: '', isSearchExpanded: false }))
                    }
                  />
                }
              />
            ) : (
              <IconButton
                IconComponent={MagnifyingGlass}
                size='sm'
                variant='ghost'
                className='text-zinc-500'
                onClick={() => setUiState(prev => ({ ...prev, isSearchExpanded: true }))}
                aria-label='Search reports'
              />
            )}
            <GenericSelectionModal
              options={sortOptions}
              selectedKey={uiState.sortOrder}
              onSelect={option => setUiState(prev => ({ ...prev, sortOrder: option.key }))}
              placeholderComponent={
                <IconButton IconComponent={SortAscending} size='sm' variant='ghost' className='text-zinc-500' />
              }
              showSearch={false}
              align='end'
              triggerClassName='!border-0'
              contentClassName='w-[180px]'
            />
            <div className='mx-2 h-4 w-px bg-zinc-200' />
            <GenerateReportsButton
              searchQuery={uiState.searchQuery}
              onSearchChange={query => setUiState(prev => ({ ...prev, searchQuery: query }))}
              onReportSelect={handleReportSelect}
            />
          </div>
        </div>

        <GoogleSheetsBanner />

        <div className='sticky z-[9] bg-zinc-50 pb-4' style={{ top: headerHeight }}>
          <ReportsFilterBar
            typeValue={uiState.reportTypeFilter}
            onTypeChange={handleTypeFilterChange}
            frequencyValue={uiState.frequencyFilter}
            onFrequencyChange={handleFrequencyFilterChange}
          />
        </div>

        <CreateReportDialog
          isOpen={uiState.showExportDialog}
          onClose={handleClose}
          isEdit={uiState.isEditMode}
          config={config}
          onConfigChange={setConfig}
          columns={columnData ?? []}
          isLoadingColumns={isValidating}
          isSubmitting={uiState.isSubmitting}
          onSubmit={uiState.isEditMode ? handleEditSubmit : handleSubmit}
        />

        <ReportList
          report={reports}
          isLoading={isInitialLoading}
          onPauseClick={({ reportId, runId }) =>
            setUiState(prev => ({
              ...prev,
              dialogConfig: {
                isOpen: true,
                type: 'pause',
                reportId,
                runId
              }
            }))
          }
          onResumeClick={({ reportId, runId }) =>
            setUiState(prev => ({
              ...prev,
              dialogConfig: {
                isOpen: true,
                type: 'resume' as const,
                reportId,
                runId
              }
            }))
          }
          hasMore={hasMore}
          onLoadMore={() => setSize(size + 1)}
          isLoadingMore={isLoadingMore}
          mutateReports={mutateReports}
          onEdit={handleEditReport}
          onSetupRecurring={handleSetupRecurring}
          highlightedReportId={uiState.highlightedReportId}
        />

        <PauseReportDialog
          isOpen={uiState.dialogConfig.isOpen}
          type={uiState.dialogConfig.type}
          reportId={uiState.dialogConfig.reportId}
          onClose={() =>
            setUiState(prev => ({
              ...prev,
              dialogConfig: { ...prev.dialogConfig, isOpen: false }
            }))
          }
          onSuccess={() => mutateReports()}
        />
      </div>
    </ReportProvider>
  )
}

export default DownloadsPage
